import {
  type AllNomineesByCategoryResponse,
  getAllNomineesByCategory,
  getAllNomineesByEdition,
  type NomineeDetailResponse,
} from '@api/nominees-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

interface UseNomineesResult {
  nominees: NomineeDetailResponse[];
  editionCategoryId: string | null;
  categoryName: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Loads every nominee of an edition, or of one of its categories. Not a page of
 * them: the screens show the whole standings and count every nominee and vote.
 */
export function useNominees(
  editionId: string | undefined,
  categoryId?: string | undefined,
): UseNomineesResult {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['nominees', editionId, categoryId] as const,
    [editionId, categoryId],
  );

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async (): Promise<AllNomineesByCategoryResponse> => {
      if (categoryId) {
        return getAllNomineesByCategory(editionId!, categoryId);
      }
      const nominees = await getAllNomineesByEdition(editionId!);
      return { data: nominees, editionCategoryId: null, categoryName: null };
    },
    enabled: !!editionId,
  });

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    nominees: data?.data ?? [],
    editionCategoryId: data?.editionCategoryId ?? null,
    categoryName: data?.categoryName ?? null,
    isLoading: !!editionId && isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load nominees') : null,
    refetch,
  };
}
